import React, {useCallback, useState} from "react";
import CustomModal from "../CustomModal/CustomModal";
import EditSectionModalBody from "./EditSectionModalBody";
import { useStoreRoom, useStoreSystem } from "../../../store";
import { useAlerts } from "../../../hooks/useAlerts";
import { SectionHeights, SectionWidth } from "../../../shared";
import { useShelvingStore } from "../../../store/useShelvingStore";
import { SectionInterface } from "../../../store/types/store";
import { getRackHeightValidation } from "./getRackHeightValidation";

type EditSectionModalType = {
    editSection: SectionInterface
}

const EditSectionModal: React.FunctionComponent<EditSectionModalType> = ({ editSection }) => {
    const { addReadyAlert } = useAlerts();

    const { roomHeight } = useStoreRoom((state) => ({
        roomHeight: state.height,
    }));

    const { sectionEditModal, setSectionEditModal } = useStoreSystem((state) => ({
        sectionEditModal: state.sectionEditModal,
        setSectionEditModal: state.setSectionEditModal,
    }));

    const { getCurrentShelf, updateSection } = useShelvingStore((state) => ({
        getCurrentShelf: state.getCurrentShelving,
        updateSection: state.updateSection,
    }));

    const currentShelf = getCurrentShelf();

    const [activeWidth, setActiveWidth] = useState<SectionWidth>(editSection.width);
    const [activeHeight, setActiveHeight] = useState<SectionHeights>({
        left: editSection.leftRackHeight,
        right: editSection.height,
    });

    const getPrevSection = useCallback(() => {
        // Берём актуальный store, а не snapshot, сохранённый в sectionEditModal.section.
        const shelf = getCurrentShelf();
        const currentSectionIndex = shelf.sections.findIndex((section) => section.id === editSection.id);

        if (currentSectionIndex <= 0) {
            return null;
        }

        return shelf.sections[currentSectionIndex - 1];
    }, [getCurrentShelf, editSection.id]);

    const checkAvailableSectionsWidth = useCallback((isRemoveSection: boolean = false) => {
        const shelf = getCurrentShelf();
        const actualSection = shelf.sections.find((section) => section.id === editSection.id) ?? editSection;

        const sectionsWidth = shelf.sections.reduce((acc, section) => {
            acc += section.id !== editSection.id ? section.width : activeWidth;
            return acc;
        }, 0);

        if (sectionsWidth > shelf.length) {
            addReadyAlert(
                `Ширина всех секций больше допустимой длины стеллажной системы`,
                "error",
                5000
            );
            return false;
        }

        if (actualSection.items.length > 0 && activeWidth !== actualSection.width) {
            addReadyAlert(
                `Минимальная ширина секции ${actualSection.width} мм с учетом наполнения.
                    Пожалуйста, удалите наполнение прежде, чем изменять ширину`,
                "error",
                5000
            );
            return false;
        }

        const prevSection = getPrevSection();
        const itemWithLargerWidth = prevSection?.items.find((item) => item.width > prevSection.width);

        if (prevSection && itemWithLargerWidth && activeWidth !== actualSection.width) {
            addReadyAlert(
                `Минимальная ширина секции ${actualSection.width} мм с учетом наполнения предыдущей секции.
                        Пожалуйста, удалите наполнение из предыдущей секции прежде, чем изменять ширину`,
                "error",
                5000
            );
            return false;
        }

        if (prevSection && itemWithLargerWidth && isRemoveSection) {
            addReadyAlert(
                `В предыдущей секции есть наполнение, которое занимает текущую секцию.
                        Пожалуйста, удалите наполнение из предыдущей секции прежде, чем удалять текущую секцию`,
                "error",
                5000
            );
            return false;
        }

        return true;
    }, [addReadyAlert, activeWidth, editSection, getCurrentShelf, getPrevSection]);

    const onCloseEditSectionModal = () => {
        if (!checkAvailableSectionsWidth()) {
            return;
        }

        // Важно брать свежую секцию из store. После первого сохранения соседние
        // leftRackHeight/height синхронизируются, поэтому snapshot из момента открытия
        // может уже не соответствовать текущему состоянию.
        const latestShelf = getCurrentShelf();
        const currentSectionIndex = latestShelf.sections.findIndex((section) => section.id === editSection.id);
        const actualSection = latestShelf.sections[currentSectionIndex];

        if (!actualSection) {
            closeModal();
            return;
        }

        const currentSectionNumber = currentSectionIndex + 1;
        const rackValidation = getRackHeightValidation({
            sections: latestShelf.sections,
            currentSectionId: actualSection.id,
            roomHeight,
            offsetCeiling: latestShelf.offsetCeiling,
            leftHeight: activeHeight.left,
            rightHeight: activeHeight.right,
        });

        const leftError = rackValidation.left.isTooShort;
        const rightError = rackValidation.right.isTooShort;

        if (leftError && rightError) {
            addReadyAlert(
                `В секции № ${currentSectionNumber} \nМинимальная высота левой стойки - ${rackValidation.left.minHeightMm} мм \nМинимальная высота правой стойки - ${rackValidation.right.minHeightMm} мм`,
                "error",
                5000
            );
            return;
        }

        if (leftError) {
            addReadyAlert(
                `Минимальная высота левой стойки в секции № ${currentSectionNumber} - ${rackValidation.left.minHeightMm} мм`,
                "error",
                5000
            );
            return;
        }

        if (rightError) {
            addReadyAlert(
                `Минимальная высота правой стойки в секции № ${currentSectionNumber} - ${rackValidation.right.minHeightMm} мм`,
                "error",
                5000
            );
            return;
        }

        updateSection({
            ...actualSection,
            leftRackHeight: activeHeight.left,
            height: activeHeight.right,
            width: activeWidth,
        });
        closeModal();
    };

    const closeModal = () => {
        setSectionEditModal({ isOpen: false, section: undefined });
    };

    return (
        <CustomModal
            isOpenModal={sectionEditModal.isOpen && sectionEditModal?.type === "edit"}
            onCloseModalCallback={onCloseEditSectionModal}
        >
            <EditSectionModalBody
                setActiveWidth={setActiveWidth}
                activeWidth={activeWidth}
                activeHeight={activeHeight}
                setActiveHeight={setActiveHeight}
                currentSection={editSection}
                closeModal={closeModal}
                roomHeight={roomHeight}
                currentShelf={currentShelf}
                checkAvailableSectionsWidth={checkAvailableSectionsWidth}
            />
        </CustomModal>
    );
};

export default EditSectionModal;
